import { use, useId, useState } from "react";
import { useNavigate } from "react-router";
import type { ReviewGate } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { HoverTip } from "@/components/ui/HoverTip";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { Note } from "@/components/ui/Note";
import { Panel } from "@/components/ui/Panel";
import { SlideToConfirm } from "@/components/ui/SlideToConfirm";
import { useStartRun } from "@/hooks/use-generate";
import { TerminologyContext } from "../../terms/Terminology";
import { ApiError, refusalDetail } from "@/services/api-client";
import {
  FIGURE_SUB_STEPS,
  INTAKE_SUB_STEPS,
  NOVELTY_TABS,
  THESIS_SUB_STEPS,
  findStage,
  openQuestionsPath,
  stagePath,
  subStepPath,
  tabPath,
} from "../../stage-vocabulary";
import { EditLink, type EditTarget } from "./ReviewCard";

function labelOf(steps: readonly { id: string; label: string }[], id: string) {
  return steps.find((step) => step.id === id)?.label ?? id;
}

/**
 * Where a shut gate is opened. The stage gate's criteria, and the invention's
 * settlement, are each met on one Thesis page, and the figure review on Review
 * Figures, so they lead to that page; every other gate leads to its stage.
 */
function whereOpened(taskId: string, gate: ReviewGate): EditTarget {
  switch (gate.gate) {
    case "element_confirmed":
    case "invention_settled":
      return {
        label: labelOf(THESIS_SUB_STEPS, "invention"),
        to: subStepPath(taskId, "thesis", "invention"),
      };
    case "position_confirmed":
    case "assessment_accepted": {
      const tab = gate.gate === "position_confirmed" ? "positions" : "analysis";
      return {
        label: labelOf(NOVELTY_TABS, tab),
        to: tabPath(taskId, "thesis", "novelty", tab),
      };
    }
    case "classification":
      return {
        label: labelOf(INTAKE_SUB_STEPS, "classification"),
        to: subStepPath(taskId, "intake", "classification"),
      };
    case "figures_reviewed":
      return {
        label: labelOf(FIGURE_SUB_STEPS, "review-insert"),
        to: subStepPath(taskId, "figures", "review-insert"),
      };
    case "questions_answered":
      return { label: "Open Questions", to: openQuestionsPath(taskId) };
    default:
      return {
        label: findStage(gate.stage)?.label ?? gate.stage,
        to: stagePath(taskId, gate.stage),
      };
  }
}

/** The gates still shut, each with the way to the stage that opens it. */
function StillToDo({ taskId, gates }: { taskId: string; gates: ReviewGate[] }) {
  const id = useId();
  return (
    <div className="mt-4">
      <MicroLabel as="h3" id={id} className="mb-2">
        Still to do before you generate
      </MicroLabel>
      <ul aria-labelledby={id} className="space-y-1.5 text-[13px]">
        {gates.map((gate) => (
          <li
            key={gate.gate}
            className="flex flex-wrap items-baseline justify-between gap-x-4"
          >
            <span>{gate.label}</span>
            <EditLink target={whereOpened(taskId, gate)} />
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Generate, held with its reason until the content is confirmed, and then the way to the draft being made. */
function GenerateButton({
  locked,
  onGo,
}: {
  locked: boolean;
  onGo: () => void;
}) {
  if (locked) {
    return (
      <Button type="button" variant="confirm" onClick={onGo}>
        Generate patent draft
      </Button>
    );
  }
  return (
    <HoverTip tip="Slide to confirm the content first." wide={false}>
      {({ triggerProps }) => (
        <Button type="button" held {...triggerProps}>
          Generate patent draft
        </Button>
      )}
    </HoverTip>
  );
}

/**
 * The slide that starts the run, and Generate. The slide freezes the inputs:
 * the server checks every gate on the state it freezes and queues the run, and
 * only then does the slide read "Content confirmed" and Generate go on to the
 * draft being made. A refusal puts the slide back and says why; a run already
 * under way is the one to go to.
 */
function StartControls({
  taskId,
  ready,
  openItemsAddress,
}: {
  taskId: string;
  ready: boolean;
  openItemsAddress: string;
}) {
  const navigate = useNavigate();
  const start = useStartRun(taskId);
  // The terms as the review showed them, for the run's lock to count the edits made from here.
  const shownTerms = use(TerminologyContext)?.review.shown ?? null;
  const [attempt, setAttempt] = useState(0);
  const toRun = () => void navigate(subStepPath(taskId, "generate", "ready"));
  const begin = () => {
    start.mutate(
      { openItemsAddress, terms: shownTerms },
      {
        onError: (error) => {
          if (error instanceof ApiError && error.code === "DRAFT_RUN_ACTIVE") {
            toRun();
            return;
          }
          setAttempt((n) => n + 1);
        },
      },
    );
  };
  const locked = start.isSuccess;
  return (
    <>
      <SlideToConfirm
        key={attempt}
        className="mx-auto mt-5"
        label="Slide to confirm the content for this draft"
        doneLabel="Content confirmed"
        confirmed={locked}
        held={!ready || start.isPending}
        onConfirm={begin}
      />
      {start.isError && (
        <Note tone="destructive" role="alert" className="mt-3 text-center">
          {refusalDetail(
            start.error,
            "The draft could not be started. Try again.",
          )}
        </Note>
      )}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
        <GenerateButton locked={locked} onGo={toRun} />
        {!locked && (
          <span className="text-xs text-muted-foreground">
            {start.isPending
              ? "Confirming the content…"
              : ready
                ? "Slide to confirm first."
                : "Settle the items above first."}
          </span>
        )}
      </div>
    </>
  );
}

/**
 * The last act on the page: the attorney confirms what they read, then
 * generates. The slide is held while a gate is shut. Questions still open do
 * not hold it: they go into the draft as they are, and the attorney is told so
 * before the slide.
 */
export function GenerateConfirm({
  taskId,
  ready,
  gates,
  openItemsAddress,
}: {
  taskId: string;
  ready: boolean;
  gates: ReviewGate[];
  openItemsAddress: string;
}) {
  const shut = gates.filter((gate) => !gate.met);
  return (
    <Panel as="section" aria-label="Generate" className="mt-6">
      <h2 className="text-lg font-extrabold tracking-tight">
        Please confirm that all content on this screen is correct before
        generating the draft.
      </h2>
      <p className="mt-1.5 max-w-[62ch] text-[13px] text-muted-foreground">
        The draft uses the content on this screen as it is now. A change you
        make after you generate goes into a draft only when you generate again.
      </p>
      {shut.length > 0 && <StillToDo taskId={taskId} gates={shut} />}
      <StartControls
        taskId={taskId}
        ready={ready}
        openItemsAddress={openItemsAddress}
      />
    </Panel>
  );
}
